import { useState, type FormEvent } from 'react'
import { Loader2, Route } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { setAdminToken, type AuthMode } from '@/lib/auth'
import { api } from '@/lib/api'

interface LandingPageProps {
  onAuthenticated: (mode: AuthMode, projectCode: string | null) => void
}

export function LandingPage({ onAuthenticated }: LandingPageProps) {
  const [code, setCode] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()

    const trimmedCode = code.trim()
    if (!trimmedCode) return

    setError(null)
    setLoading(true)

    try {
      const result = await api.validateCode(trimmedCode)
      if (result.mode === 'admin') {
        setAdminToken(trimmedCode)
        onAuthenticated('admin', null)
      } else {
        onAuthenticated('client', result.project_code ?? trimmedCode)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to validate access code')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center px-4 py-12">
      <Card className="w-full max-w-md border-white/30 bg-white/88 shadow-2xl backdrop-blur-xl">
        <CardHeader className="space-y-4 text-center">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-primary text-primary-foreground shadow-lg">
            <Route className="h-7 w-7" />
          </div>
          <CardTitle className="text-3xl">BT Travel Time</CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <label htmlFor="access-code" className="text-sm font-medium text-foreground">
                Access Code
              </label>
              <Input
                id="access-code"
                type="text"
                value={code}
                onChange={(event) => setCode(event.target.value)}
                placeholder="Project code or admin password"
                autoComplete="off"
                autoFocus
                className="bg-white/90"
              />
            </div>

            {error ? (
              <p className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                {error}
              </p>
            ) : null}

            <Button type="submit" className="w-full" disabled={loading || !code.trim()}>
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Continue'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}
